import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import {
  BellRing,
  Blocks,
  Building2,
  CreditCard,
  Cpu,
  FileKey,
  KeyRound,
  ListChecks,
  ScrollText,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { KhoaDich } from '../i18n';
import type { PhamViCong } from '../lib/pham-vi-cong';
import { QUYEN_GOI_THANH_TOAN } from '../modules/goi-thanh-toan/nghiep-vu/tab';

/**
 * REGISTRY — nguồn duy nhất của điều hướng, route và guard quyền.
 *
 * **Thêm màn hình = thêm một dòng ở đây + một thư mục trong `src/modules/`.**
 * Không sửa `routes/`, không hardcode menu ở nơi khác. Ba thứ đó đọc chung một
 * danh sách nên không bao giờ lệch nhau.
 *
 * Registry này dành cho **nhóm quản trị nền tảng** (tenant, gói, hàng đợi…),
 * tức phạm vi `nen-tang`. Cây điều hướng nghiệp vụ của FOXAI Portal — phạm vi
 * `tenant` — vẫn ở `components/layout/nav-config.ts` cho tới khi chốt hướng xử
 * lý ở mục 3 của ADR-UI-0003.
 *
 * Hai phạm vi **không trộn vào nhau**: tài khoản nền tảng không thấy màn
 * nghiệp vụ, tài khoản tenant không thấy màn quản trị. Xem `lib/pham-vi-cong`.
 */
export interface ModuleDef {
  /** Slug không dấu, vừa là id vừa là path. */
  id: string;
  /** Khoá dịch của nhãn hiện ở điều hướng và breadcrumb. */
  khoaNhan: KhoaDich;
  icon: LucideIcon;
  /** Khoá dịch của mô tả ngắn, dùng cho tooltip và command palette sau này. */
  khoaMo?: KhoaDich;
  Component: LazyExoticComponent<ComponentType>;
  /** Màn chi tiết dưới `/<id>/:thamSo`; bỏ trống thì mục không có màn con. */
  ComponentCon?: LazyExoticComponent<ComponentType>;
  /** Tên tham số của màn chi tiết. */
  thamSo?: string;
  /**
   * Quyền `PHAN_HE.HANH_DONG` cần có để thấy mục này. Danh sách nghĩa là **một trong các quyền** — dành cho màn gom
   * nhiều tab, mỗi tab một quyền đọc (màn tự ẩn tab người dùng không có quyền, ADR-UI-0005).
   */
  quyen: string | readonly string[];
  /**
   * Cổng mà màn này thuộc về.
   *
   * Hôm nay mọi mục trong registry đều là `nen-tang`; trường vẫn khai tường
   * minh để thêm một màn của cổng tenant vào đây là chuyện khai một dòng, chứ
   * không phải sửa lại luật lọc ở ba nơi.
   */
  phamVi: PhamViCong;
}

/**
 * `lazy()` có tự phục hồi chunk.
 *
 * `React.lazy` trần cho màn hình trắng sau mỗi lần deploy: tab đang mở đi xin
 * chunk mang hash cũ mà build mới đã xóa, `import()` ném, không ai bắt. Ở đây
 * tải lại trang **đúng một lần**; cờ trong `sessionStorage` chặn vòng lặp reload
 * vô tận khi lỗi là thật chứ không phải do deploy.
 */
const CO_DA_TAI_LAI = 'admin-da-tai-lai';

export function nhapModule(
  tai: () => Promise<{ default: ComponentType }>,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => {
    try {
      const m = await tai();
      try {
        sessionStorage.removeItem(CO_DA_TAI_LAI);
      } catch {
        // Trình duyệt chặn sessionStorage: bỏ qua, chỉ mất khả năng tự phục hồi.
      }
      return m;
    } catch (loi) {
      let daThu = true;
      try {
        daThu = sessionStorage.getItem(CO_DA_TAI_LAI) === '1';
        if (!daThu) sessionStorage.setItem(CO_DA_TAI_LAI, '1');
      } catch {
        daThu = true;
      }
      if (!daThu) {
        window.location.reload();
        // Promise không bao giờ hoàn tất: trang đang tải lại, dựng màn lỗi
        // trong vài trăm ms đó chỉ làm nhấp nháy.
        return new Promise<{ default: ComponentType }>(() => {});
      }
      throw loi; // Tải lại rồi vẫn hỏng → để error boundary lo.
    }
  });
}

export const MODULES: ModuleDef[] = [
  {
    id: 'tenant',
    khoaNhan: 'module.tenant.nhan',
    icon: Building2,
    khoaMo: 'module.tenant.mo',
    Component: nhapModule(() =>
      import('../modules/tenant/views/TenantListView').then((m) => ({
        default: m.TenantListView,
      })),
    ),
    ComponentCon: nhapModule(() =>
      import('../modules/tenant/views/TenantDetailView').then((m) => ({
        default: m.TenantDetailView,
      })),
    ),
    thamSo: 'tenantId',
    quyen: 'TENANT.READ',
    phamVi: 'nen-tang',
  },
  {
    /*
     * Gói & thanh toán: một màn sáu tab thay bốn mục cũ (Gói dịch vụ, Gói tài nguyên, Yêu cầu token AI, Giá AI).
     * Thấy mục khi có quyền đọc của **ít nhất một** tab; từng tab tự lọc theo quyền của nó
     * (`modules/goi-thanh-toan/nghiep-vu/tab.ts`). Đường dẫn cũ chuyển hướng qua `DUONG_CU` ở cuối tệp.
     */
    id: 'goi-thanh-toan',
    khoaNhan: 'module.goiThanhToan.nhan',
    icon: CreditCard,
    khoaMo: 'module.goiThanhToan.mo',
    Component: nhapModule(() =>
      import('../modules/goi-thanh-toan/views/GoiThanhToanView').then((m) => ({ default: m.GoiThanhToanView })),
    ),
    quyen: QUYEN_GOI_THANH_TOAN,
    phamVi: 'nen-tang',
  },
  {
    // Plugin Platform — module = plugin (Q-04, ADR-0045/0046/0048). Nộp gói (VENDOR),
    // duyệt/từ chối (PLATFORM_ADMIN). Khác tab Gói dịch vụ của `goi-thanh-toan`: đây là tính năng mở rộng cài
    // theo tenant, không phải gói thuê bao. Quyết định mở cho bên thứ ba chưa có ADR
    // chính thức, mới là đề xuất đang chờ chốt.
    id: 'quan-ly-module',
    khoaNhan: 'module.quanLyModule.nhan',
    icon: Blocks,
    khoaMo: 'module.quanLyModule.mo',
    Component: nhapModule(() =>
      import('../modules/quan-ly-module/views/DanhSachModuleView').then((m) => ({
        default: m.DanhSachModuleView,
      })),
    ),
    ComponentCon: nhapModule(() =>
      import('../modules/quan-ly-module/views/ChiTietModuleView').then((m) => ({
        default: m.ChiTietModuleView,
      })),
    ),
    thamSo: 'code',
    quyen: 'MODULE_REGISTRY.READ',
    phamVi: 'nen-tang',
  },
  {
    // ADR-0083: danh mục model AI cấp nền tảng; tắt từng model cho từng tenant ở chi tiết tenant.
    id: 'model-ai',
    khoaNhan: 'module.modelAi.nhan',
    icon: Cpu,
    khoaMo: 'module.modelAi.mo',
    Component: nhapModule(() =>
      import('../modules/ai-model/views/AiModelListView').then((m) => ({ default: m.AiModelListView })),
    ),
    quyen: 'AI_MODEL.READ',
    phamVi: 'nen-tang',
  },
  {
    // ADR-0058 mục 8.3: license on-premise, hàng chờ duyệt, keyring.
    id: 'license',
    khoaNhan: 'module.license.nhan',
    icon: FileKey,
    khoaMo: 'module.license.mo',
    Component: nhapModule(() =>
      import('../modules/license/views/LicenseView').then((m) => ({
        default: m.LicenseView,
      })),
    ),
    quyen: 'LICENSE.READ',
    phamVi: 'nen-tang',
  },
  {
    id: 'hang-doi',
    khoaNhan: 'module.hangDoi.nhan',
    icon: ListChecks,
    khoaMo: 'module.hangDoi.mo',
    Component: nhapModule(() =>
      import('../modules/sap-co/SapCo').then((m) => ({ default: m.HangDoiView })),
    ),
    quyen: 'VIEC.READ',
    phamVi: 'nen-tang',
  },
  {
    id: 'nhat-ky',
    khoaNhan: 'module.nhatKy.nhan',
    icon: ScrollText,
    khoaMo: 'module.nhatKy.mo',
    // ADR-0059 mục 8: nhật ký thao tác thật, thay chỗ `SapCo`.
    Component: nhapModule(() =>
      import('../modules/nhat-ky/views/NhatKyHeThongView').then((m) => ({
        default: m.NhatKyHeThongView,
      })),
    ),
    quyen: 'AUDIT_EVENTS.READ',
    phamVi: 'nen-tang',
  },
  {
    // ADR-0059 mục 8: chính sách theo gói, mật khẩu, blocklist nền tảng, người nhận thông báo.
    id: 'chinh-sach-nen-tang',
    khoaNhan: 'module.chinhSachNenTang.nhan',
    icon: ShieldCheck,
    khoaMo: 'module.chinhSachNenTang.mo',
    Component: nhapModule(() =>
      import('../modules/chinh-sach-nen-tang/views/ChinhSachNenTangView').then((m) => ({
        default: m.ChinhSachNenTangView,
      })),
    ),
    quyen: 'PLATFORM_POLICY.READ',
    phamVi: 'nen-tang',
  },
  {
    // ADR-0102: trung tâm thông báo của nền tảng — kênh, tác vụ cảnh báo của đội vận hành, danh mục theo gói, giám sát
    // gửi. notification-service chỉ nhận PLATFORM_ADMIN ở nhóm route nền tảng, nên dùng mã chỉ PLATFORM_ADMIN có.
    id: 'canh-bao',
    khoaNhan: 'canhBao.nhanNav',
    icon: BellRing,
    khoaMo: 'canhBao.moNav',
    Component: nhapModule(() => import('../modules/canh-bao/views/CanhBaoNenTangView')),
    quyen: 'PLATFORM_POLICY.UPDATE',
    phamVi: 'nen-tang',
  },
  {
    // ADR-0059 mục 3: mức log tạm thời từng service/logger, tự hết hạn. Chỉ PLATFORM_ADMIN
    // (AUDITOR chỉ có PLATFORM_POLICY.READ nên không thấy, khớp với 403 của backend).
    id: 'muc-log',
    khoaNhan: 'module.mucLog.nhan',
    icon: SlidersHorizontal,
    khoaMo: 'module.mucLog.mo',
    Component: nhapModule(() =>
      import('../modules/muc-log/views/MucLogView').then((m) => ({ default: m.MucLogView })),
    ),
    quyen: 'PLATFORM_POLICY.UPDATE',
    phamVi: 'nen-tang',
  },
  {
    id: 'nguoi-dung',
    khoaNhan: 'module.nguoiDung.nhan',
    icon: Users,
    khoaMo: 'module.nguoiDung.mo',
    Component: nhapModule(() =>
      import('../modules/nguoi-dung-nen-tang/views/NhanSuNenTangView').then((m) => ({
        default: m.NhanSuNenTangView,
      })),
    ),
    quyen: 'USER_CONFIG.READ',
    phamVi: 'nen-tang',
  },
  {
    id: 'vai-tro',
    khoaNhan: 'module.vaiTro.nhan',
    icon: Shield,
    khoaMo: 'module.vaiTro.mo',
    Component: nhapModule(() =>
      import('../modules/vai-tro-nen-tang/views/VaiTroNenTangView').then((m) => ({
        default: m.VaiTroNenTangView,
      })),
    ),
    quyen: 'ROLE_CONFIG.READ',
    phamVi: 'nen-tang',
  },
  {
    id: 'khoa-api',
    khoaNhan: 'module.khoaApi.nhan',
    icon: KeyRound,
    khoaMo: 'module.khoaApi.mo',
    Component: nhapModule(() =>
      import('../modules/sap-co/SapCo').then((m) => ({ default: m.KhoaApiView })),
    ),
    quyen: 'API_KEY.READ',
    phamVi: 'nen-tang',
  },
];

export const timModule = (id: string): ModuleDef | undefined =>
  MODULES.find((m) => m.id === id);

/**
 * Mục một người được thấy — điều hướng và guard route dùng chung **một** luật.
 *
 * Hai cửa, phải qua cả hai: màn phải thuộc **đúng cổng** của người này, rồi
 * mới xét quyền. Bỏ cửa phạm vi thì `SUPER_ADMIN` của nền tảng (vai trò mở mọi
 * quyền) lại thấy hết màn nghiệp vụ của tenant.
 *
 * `phamVi` để trống thì không lọc theo cổng — nhánh dành cho nơi gọi chưa biết
 * cổng, ví dụ test đơn vị của riêng luật quyền.
 */
export const duocVao = (
  m: ModuleDef,
  duoc: (ma: string) => boolean,
  phamVi?: PhamViCong,
): boolean => {
  if (phamVi !== undefined && m.phamVi !== phamVi) return false;
  return typeof m.quyen === 'string' ? duoc(m.quyen) : m.quyen.some(duoc);
};

/**
 * Đường dẫn cũ còn trong dấu trang, email, thông báo — chuyển sang đúng tab của màn mới thay vì ra 404.
 *
 * `routes/index.tsx` sinh route chuyển hướng từ danh sách này (cả `/<tu>` lẫn `/<tu>/*`), nên gộp hay đổi tên màn
 * là thêm một dòng ở đây, không sửa tệp route. `thamSo` ghi đè lên query của đường cũ; query khác giữ nguyên.
 * Quyền do màn đích kiểm: chuyển hướng trước, `RouteModule` chặn sau — một luật, một nơi.
 */
export interface DuongCu {
  /** Đoạn đường dẫn cũ dưới `/admin`, không có `/` đầu. */
  tu: string;
  /** `id` của module đích. */
  den: string;
  thamSo?: Readonly<Record<string, string>>;
}

export const DUONG_CU: readonly DuongCu[] = [
  { tu: 'goi-dich-vu', den: 'goi-thanh-toan', thamSo: { tab: 'goi-dich-vu' } },
  { tu: 'goi-tai-nguyen', den: 'goi-thanh-toan', thamSo: { tab: 'goi-han-muc' } },
  { tu: 'gia-ai', den: 'goi-thanh-toan', thamSo: { tab: 'bang-gia-model' } },
  { tu: 'yeu-cau-token', den: 'goi-thanh-toan', thamSo: { tab: 'yeu-cau-nap-token' } },
];

/**
 * Nơi đưa người dùng tới khi mục họ gõ không vào được.
 *
 * **Không cố định là mục đầu danh sách**: người chỉ có quyền nhật ký mà bị đẩy
 * về "Tenant" thì lại bị đẩy tiếp — vòng chuyển hướng vô tận. Phải là mục đầu
 * tiên *người này* được vào.
 */
export const moduleDauTien = (
  duoc: (ma: string) => boolean,
  phamVi?: PhamViCong,
): ModuleDef | undefined => MODULES.find((m) => duocVao(m, duoc, phamVi));
